import {
  Button,
  QuickPanelDrawer,
  QuickPanelDrawerActions,
  QuickPanelDrawerHeader,
  QuickPanelDrawerIcon,
  QuickPanelDrawerMain,
  QuickPanelDrawerTitle,
  QuickPanelStack,
  QuickPanelTrail,
  QuickPanelTrailCrumb,
  QuickPanelTrailSeparator,
  ShellIconEdit,
  ShellIconLayers,
  ShellIconPackage,
  ShellIconX,
  Spinner,
  type ShellStrokeIconProps,
} from '@zrsystem/ui';
import { Fragment, Suspense, type ComponentType } from 'react';

import { QuickPanelEntryProvider } from '@/app/surfaces/quick-panel-entry.js';
import { QuickPanelFooterSlotProvider } from '@/app/surfaces/quick-panel-footer-slot.js';
import { useQuickPanelContext } from '@/app/surfaces/quick-panel-provider.js';
import { quickPanelRegistry } from '@/app/surfaces/quick-panel-registry.js';
import {
  QuickPanelTrailEnd,
  QuickPanelTrailSlotProvider,
} from '@/app/surfaces/quick-panel-trail-slot.js';
import {
  resolvePanelTitle,
  resolveQuickPanelTrailCrumbsForEntry,
} from '@/app/surfaces/resolve-quick-panel-trail.js';

const QUICK_PANEL_ICONS: Partial<Record<string, ComponentType<ShellStrokeIconProps>>> = {
  'catalog-taxonomy-list': ShellIconLayers,
  'catalog-taxonomy-form': ShellIconEdit,
  'vehicle-brands-list': ShellIconLayers,
  'vehicle-models-list': ShellIconLayers,
  'vehicle-catalog-item-form': ShellIconEdit,
  'catalog-generic-register': ShellIconPackage,
  'catalog-generic-edit': ShellIconEdit,
  'catalog-product-link-generic': ShellIconPackage,
  'catalog-product-attributes': ShellIconEdit,
  'catalog-product-direct-register': ShellIconPackage,
  'catalog-product-direct-edit': ShellIconEdit,
  'catalog-manufacturer-register': ShellIconPackage,
  'catalog-manufacturer-edit': ShellIconEdit,
  'catalog-manufacturer-archive': ShellIconEdit,
  'catalog-generic-archive': ShellIconEdit,
  'catalog-generic-service-register': ShellIconPackage,
  'catalog-generic-service-edit': ShellIconEdit,
  'catalog-generic-service-archive': ShellIconEdit,
  'catalog-product-applicability-register': ShellIconPackage,
  'catalog-product-applicability-edit': ShellIconEdit,
  'catalog-product-applicability-archive': ShellIconEdit,
};

export function QuickPanelHost() {
  const { stack, dismissQuickPanelById, togglePin } = useQuickPanelContext();

  if (stack.length === 0) {
    return null;
  }

  return (
    <QuickPanelStack aria-label="Pilha de painéis abertos">
      {stack.map((entry, entryIndex) => {
        const registryEntry = quickPanelRegistry[entry.key];
        if (!registryEntry) {
          return null;
        }

        const Content = registryEntry.component;
        const trailCrumbs = resolveQuickPanelTrailCrumbsForEntry(
          stack,
          entryIndex,
          (key) => quickPanelRegistry[key],
        );
        const trail =
          trailCrumbs.length === 0 ? null : (
            <QuickPanelTrail>
              {trailCrumbs.map((crumb, trailIndex) => (
                <Fragment key={`${crumb}-${trailIndex}`}>
                  {trailIndex > 0 ? <QuickPanelTrailSeparator /> : null}
                  <QuickPanelTrailCrumb current={trailIndex === trailCrumbs.length - 1}>
                    {crumb}
                  </QuickPanelTrailCrumb>
                </Fragment>
              ))}
              <QuickPanelTrailEnd />
            </QuickPanelTrail>
          );
        const panelTitle = resolvePanelTitle(entry, registryEntry);
        const Icon = QUICK_PANEL_ICONS[entry.key];

        return (
          <QuickPanelDrawer
            key={entry.id}
            size={registryEntry.size ?? 'lg'}
            pinned={entry.pinned}
            stackIndex={entryIndex}
            aria-label={panelTitle}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <QuickPanelDrawerHeader>
              {Icon ? (
                <QuickPanelDrawerIcon>
                  <Icon size={13} strokeWidth={2.25} />
                </QuickPanelDrawerIcon>
              ) : null}
              <QuickPanelDrawerTitle>
                <span className="qp-drawer-title-text">{panelTitle}</span>
              </QuickPanelDrawerTitle>
              <QuickPanelDrawerActions>
                <Button
                  variant="ghost"
                  size="xs"
                  iconOnly
                  title={entry.pinned ? 'Desafixar' : 'Fixar (mantém aberto entre telas)'}
                  aria-label={entry.pinned ? 'Desafixar painel' : 'Fixar painel'}
                  aria-pressed={entry.pinned}
                  onClick={() => {
                    togglePin(entry.id);
                  }}
                >
                  {entry.pinned ? '▪' : '○'}
                </Button>
                <Button
                  variant="ghost"
                  size="xs"
                  iconOnly
                  title="Fechar (Esc)"
                  aria-label="Fechar painel"
                  onClick={() => {
                    dismissQuickPanelById(entry.id);
                  }}
                >
                  <ShellIconX size={13} />
                </Button>
              </QuickPanelDrawerActions>
            </QuickPanelDrawerHeader>

            <QuickPanelEntryProvider entryId={entry.id} entryKey={entry.key}>
              <QuickPanelTrailSlotProvider>
                <QuickPanelFooterSlotProvider>
                  <QuickPanelDrawerMain trail={trail}>
                    <Suspense fallback={<Spinner aria-label="Carregando painel" />}>
                      <Content {...entry.params} />
                    </Suspense>
                  </QuickPanelDrawerMain>
                </QuickPanelFooterSlotProvider>
              </QuickPanelTrailSlotProvider>
            </QuickPanelEntryProvider>
          </QuickPanelDrawer>
        );
      })}
    </QuickPanelStack>
  );
}
